/* eslint-disable react/jsx-no-useless-fragment */
import * as React from 'react';
import { Config } from '@dispatch/web/config';
import {
  AuthenticationResult,
  InteractionRequiredAuthError,
  InteractionStatus,
  RedirectRequest,
} from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { dispatchClient } from '@dispatch/shared/services';
import { getEmailFromStorage } from './EmailStorage';
import { useAccessToken } from './AccessToken';
import { getStoredAuthority } from './AuthorityStorage';
import { performErrorLogout } from './LogoutUtility';
import { selectSignInAccount } from './AccountSelection';
import { trackEvent } from '@dispatch/web/telemetry';
import invariant from 'tiny-invariant';
import { isEmpty } from 'lodash';

// Maximum number of retry attempts for token acquisition
const TOKEN_RETRY_ATTEMPTS = 3;
const TOKEN_RETRY_DELAY_MS = 500;
// Upper time bound for a single MSAL token acquisition. A hung token POST
// (network/AAD stall) must reject within this window so it reaches the retry
// loop and ultimately performErrorLogout, instead of freezing the app.
// Deliberately below the 45s HTTP timeout so token stalls surface first.
const TOKEN_ACQUISITION_TIMEOUT_MS = 25000;
// Test-only: how long FORCE_USERS_STALL waits before simulating the bootstrap
// HTTP timeout's outcome. Compressed (vs the real BOOTSTRAP_HTTP_TIMEOUT_MS)
// so the *bounded recovery* is quick to observe in DEV/UAT rather than a 45s
// look-alike of the original hang. The real 45s bound is asserted in
// DispatchClient.spec.ts.
const TEST_USERS_STALL_ABORT_MS = 2000;
// Auth test mode — enabled via NX_AUTH_TEST_MODE=true at build time.
// AUTH_TEST_MODE is a runtime value (Config.AuthTestMode), so when disabled the
// flag branches are NOT tree-shaken — they remain in the bundle but are
// runtime-gated to no-op (every flag short-circuits on AUTH_TEST_MODE === false).
// When enabled, flags are read live from localStorage — toggle via browser
// console (setting applies on the next request/operation; clearing lets the
// in-app Retry recover without a full refresh):
//   localStorage.setItem('AUTH_TEST_FORCE_TOKEN_FAILURE', 'true');
//   localStorage.removeItem('AUTH_TEST_FORCE_TOKEN_FAILURE');
const AUTH_TEST_MODE = Config.AuthTestMode;
if (AUTH_TEST_MODE) {
  // eslint-disable-next-line no-console
  console.warn(
    '[Auth] TEST MODE ENABLED — test flags are active via localStorage'
  );
}
// Flags are read LIVE from localStorage at each use (via getters), not frozen
// at module load. This lets a flag be cleared at runtime — e.g. removing
// AUTH_TEST_FORCE_USERS_STALL and clicking the in-app Retry recovers WITHOUT a
// full page refresh, and mid-session toggles take effect on the next request.
// Prod-safe: AUTH_TEST_MODE short-circuits first, so no localStorage read
// happens when the mode is off.
const isTestFlagSet = (key: string) =>
  AUTH_TEST_MODE && localStorage.getItem(key) === 'true';
const TEST_FLAGS = {
  get FORCE_MISSING_CLAIMS() {
    return isTestFlagSet('AUTH_TEST_FORCE_MISSING_CLAIMS');
  },
  get FORCE_TOKEN_FAILURE() {
    return isTestFlagSet('AUTH_TEST_FORCE_TOKEN_FAILURE');
  },
  get FORCE_RETRY_THEN_SUCCEED() {
    return isTestFlagSet('AUTH_TEST_FORCE_RETRY_THEN_SUCCEED');
  },
  get FORCE_INTERACTION_REQUIRED() {
    return isTestFlagSet('AUTH_TEST_FORCE_INTERACTION_REQUIRED');
  },
  get FORCE_CLAIMS_ALWAYS_MISSING() {
    return isTestFlagSet('AUTH_TEST_FORCE_CLAIMS_ALWAYS_MISSING');
  },
  get FORCE_EXPIRED_TOKEN_RESPONSE() {
    return isTestFlagSet('AUTH_TEST_FORCE_EXPIRED_TOKEN_RESPONSE');
  },
  // Simulate an infinite token-acquisition hang (the freeze repro). The
  // existing FORCE_TOKEN_FAILURE only throws fast; this awaits a never-
  // resolving promise so the outer TOKEN_ACQUISITION_TIMEOUT_MS bound fires.
  get FORCE_TOKEN_HANG() {
    return isTestFlagSet('AUTH_TEST_FORCE_TOKEN_HANG');
  },
  // Abort the GET /dispatch/auth/users request like the bootstrap HTTP timeout
  // would, to simulate a stalled bootstrap data call.
  get FORCE_USERS_STALL() {
    return isTestFlagSet('AUTH_TEST_FORCE_USERS_STALL');
  },
};

// Track state for test scenarios (only used when AUTH_TEST_MODE is enabled)
let testAttemptCount = 0;
let testForceRefreshCalled = false;

// Error thrown when a token acquisition exceeds TOKEN_ACQUISITION_TIMEOUT_MS.
// Distinct class so the retry loop can recognize a stall vs. an MSAL error.
class TokenAcquisitionTimeoutError extends Error {
  constructor() {
    super('Token acquisition timed out');
    this.name = 'TokenAcquisitionTimeoutError';
  }
}

/**
 * Bounds a token-acquisition promise with a hard timeout. MSAL's
 * acquireTokenSilent can hang indefinitely on a stalled token POST
 * (network/AAD), which previously froze the whole app. Racing it against a
 * timeout guarantees the promise settles so the retry loop and
 * performErrorLogout fallback can run.
 */
function withTokenTimeout<T>(promise: Promise<T>): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(
      () => reject(new TokenAcquisitionTimeoutError()),
      TOKEN_ACQUISITION_TIMEOUT_MS
    );
  });
  return Promise.race([promise, timeout]).finally(() =>
    clearTimeout(timeoutId)
  );
}

function configureAxiosDefaults(clients: AxiosInstance[]) {
  invariant(
    !!Config.ApiBaseUrl,
    'Missing API base URL, cannot configure HTTP clients'
  );
  invariant(
    !!Config.ApiSubToken,
    'Missing API subscription key, cannot configure HTTP clients'
  );

  clients.forEach((client) => {
    client.defaults.baseURL = Config.ApiBaseUrl;

    client.defaults.headers.common['Ocp-Apim-Subscription-Key'] =
      Config.ApiSubToken;
  });
}

function AxiosConfiguration({ children }: { children: React.ReactChild }) {
  const { instance, inProgress } = useMsal();
  const { accessToken, setAccessToken, setIsAccessTokenSet } = useAccessToken();

  // Reset test scenario state on mount to avoid stale state after navigation
  if (AUTH_TEST_MODE) {
    testAttemptCount = 0;
    testForceRefreshCalled = false;
  }

  // Track pending token acquisition to prevent multiple simultaneous requests
  const tokenAcquisitionPromiseRef = React.useRef<Promise<
    string | null
  > | null>(null);

  // Track when logout is in progress to block API calls during logout
  const isLoggingOutRef = React.useRef(false);

  // Track inProgress via ref so interceptor closures always read the current value.
  // Without this, the interceptor captures a stale inProgress from the render
  // that registered it (inProgress is not in the effect's dependency array).
  const inProgressRef = React.useRef(inProgress);
  inProgressRef.current = inProgress;

  /**
   * Helper function to validate token has required claims
   * extension_TenantId must be present and non-empty
   */
  const hasValidClaims = (
    claims: Record<string, unknown> | undefined
  ): boolean => {
    // Test Scenario 1: Force missing claims on first call only
    if (TEST_FLAGS.FORCE_MISSING_CLAIMS && !testForceRefreshCalled) {
      // eslint-disable-next-line no-console
      console.log('TEST: Forcing missing claims (will trigger forceRefresh)');
      return false;
    }

    // Test Scenario 5: Force claims always missing
    if (TEST_FLAGS.FORCE_CLAIMS_ALWAYS_MISSING) {
      // eslint-disable-next-line no-console
      console.log('TEST: Forcing claims to always be missing');
      return false;
    }

    if (!claims) {
      return false;
    }

    const tenantId = claims.extension_TenantId;

    const hasTenantId = !isEmpty(tenantId);

    return hasTenantId;
  };

  /**
   * Acquires a valid token using MSAL's acquireTokenSilent
   * MSAL automatically handles token expiration - it returns cached token if valid,
   * or refreshes using the refresh token if expired.
   * Includes retry logic for transient failures.
   * Returns null if token acquisition fails after all retries.
   */
  const acquireValidToken = React.useCallback(async (): Promise<
    string | null
  > => {
    if (instance.getAllAccounts().length === 0) {
      return null;
    }

    const storedAuthority = getStoredAuthority();
    if (!storedAuthority) {
      return null;
    }

    const account = selectSignInAccount(instance);
    if (!account) {
      return null;
    }

    const accessTokenRequest = {
      scopes: (Config.B2C?.scopes || '').split(', '),
      account: account || undefined,
      authority: storedAuthority || undefined,
      forceRefresh: false,
    };

    /**
     * Attempts to acquire token with optional force refresh
     * MSAL's acquireTokenSilent automatically handles token expiration:
     * - Returns cached token if still valid
     * - Refreshes using refresh token if expired
     * Returns the token response or throws an error
     */
    const attemptTokenAcquisition = async (
      forceRefresh: boolean
    ): Promise<AuthenticationResult> => {
      // Test Scenario 2: Force complete token failure
      if (TEST_FLAGS.FORCE_TOKEN_FAILURE) {
        // eslint-disable-next-line no-console
        console.log('TEST: Forcing token acquisition failure');
        throw new Error('TEST: Forced token failure');
      }

      // Test Scenario 3: Force first attempt to fail, then succeed
      // Skip on forceRefresh (recursive) calls to avoid re-entry after success
      if (TEST_FLAGS.FORCE_RETRY_THEN_SUCCEED && !forceRefresh) {
        testAttemptCount++;
        if (testAttemptCount < TOKEN_RETRY_ATTEMPTS) {
          // eslint-disable-next-line no-console
          console.log(
            `TEST: Forcing failure on attempt ${testAttemptCount}, will retry...`
          );
          throw new Error('TEST: Forced transient failure');
        }
        // eslint-disable-next-line no-console
        console.log(`TEST: Attempt ${testAttemptCount} - allowing success`);
        testAttemptCount = 0; // Reset for next cycle
      }

      // Test Scenario 4: Force InteractionRequiredAuthError
      if (TEST_FLAGS.FORCE_INTERACTION_REQUIRED) {
        // eslint-disable-next-line no-console
        console.log(
          'TEST: Forcing InteractionRequiredAuthError (redirect to login)'
        );
        throw new InteractionRequiredAuthError(
          'TEST: Forced interaction required'
        );
      }

      // Track forceRefresh for Scenario 1
      if (forceRefresh && TEST_FLAGS.FORCE_MISSING_CLAIMS) {
        // eslint-disable-next-line no-console
        console.log('TEST: forceRefresh called, claims will now be valid');
        testForceRefreshCalled = true;
      }

      // Test Scenario 7: Force an infinite token-acquisition hang to exercise
      // the outer timeout bound (the freeze repro). The hang MUST be raced by
      // withTokenTimeout — exactly like a real stalled acquireTokenSilent —
      // otherwise it sits outside the 25s bound and the timeout/retry/logout
      // chain never runs.
      if (TEST_FLAGS.FORCE_TOKEN_HANG) {
        // eslint-disable-next-line no-console
        console.log('TEST: Forcing token acquisition hang (never resolves)');
      }

      const response = await withTokenTimeout(
        TEST_FLAGS.FORCE_TOKEN_HANG
          ? new Promise<AuthenticationResult>(() => {
              /* intentionally never resolves — exercises the timeout bound */
            })
          : instance.acquireTokenSilent({
              ...accessTokenRequest,
              forceRefresh,
            })
      );

      // Test Scenario 6: Simulate an expired token by overriding expiresOn
      const effectiveExpiresOn = TEST_FLAGS.FORCE_EXPIRED_TOKEN_RESPONSE
        ? new Date(0)
        : response.expiresOn;
      if (TEST_FLAGS.FORCE_EXPIRED_TOKEN_RESPONSE) {
        // eslint-disable-next-line no-console
        console.log('TEST: Forcing expired token response (expiresOn=epoch)');
      }

      // MSAL can return stale cached tokens in background-tab / long-session
      // scenarios without throwing. Proactively force-refresh if the token
      // is within 5 minutes of expiry so we never hand an about-to-expire
      // token to APIM (which has zero clock-skew tolerance in prod today).
      if (!forceRefresh && effectiveExpiresOn) {
        const bufferMs = 5 * 60 * 1000; // 5-minute buffer
        if (effectiveExpiresOn.getTime() - Date.now() < bufferMs) {
          return attemptTokenAcquisition(true);
        }
      }

      // Validate claims are present (tenantId is required)
      const idTokenClaims = response.idTokenClaims as Record<string, unknown>;
      if (!hasValidClaims(idTokenClaims)) {
        // If claims are missing after a non-forced request, try with force refresh
        if (!forceRefresh) {
          return attemptTokenAcquisition(true);
        }
        // Claims still missing after force refresh - throw to trigger retry
        throw new Error('Token missing required claims after force refresh');
      }

      return response;
    };

    // Retry loop for token acquisition
    // Tracks whether any attempt stalled/failed so a later success can be
    // reported as a recovery.
    let hadFailedAttempt = false;
    for (let attempt = 1; attempt <= TOKEN_RETRY_ATTEMPTS; attempt++) {
      try {
        const accessTokenResponse = await attemptTokenAcquisition(false);

        const newToken = accessTokenResponse.accessToken as string;
        const expiresOn = accessTokenResponse.expiresOn as Date;

        setAccessToken({
          token: newToken,
          expiresOn: expiresOn,
        });

        if (hadFailedAttempt) {
          trackEvent('BootstrapRecovered', {
            source: 'token',
            attempt: String(attempt),
          });
        }

        return newToken;
      } catch (error) {
        // Check if this is an interaction required error - don't retry, redirect instead
        if (error instanceof InteractionRequiredAuthError) {
          const request: RedirectRequest = {
            loginHint: getEmailFromStorage() || '',
            scopes: (Config.B2C?.scopes || '').split(', '),
            prompt: 'select_account',
            authority: storedAuthority || undefined,
          };
          instance.acquireTokenRedirect(request);
          return null;
        }

        hadFailedAttempt = true;

        // A hung token POST that we timed out is the freeze signature — make
        // it observable so the failure rate can be monitored (AC #5).
        if (error instanceof TokenAcquisitionTimeoutError) {
          trackEvent('BootstrapStallDetected', {
            source: 'token',
            outcome: 'timeout',
            attempt: String(attempt),
          });
        }

        // If this is not the last attempt, continue to retry
        if (attempt < TOKEN_RETRY_ATTEMPTS) {
          // Brief delay before retry to allow transient issues to resolve
          await new Promise((resolve) =>
            setTimeout(resolve, TOKEN_RETRY_DELAY_MS)
          );
          continue;
        }

        // All retries exhausted - surface an actionable failure (AC #4/#5)
        // before forcing re-auth.
        trackEvent('BootstrapFailedActionable', {
          source: 'token',
          outcome:
            error instanceof TokenAcquisitionTimeoutError
              ? 'timeout'
              : 'exhausted',
        });

        // All retries exhausted - set logout flag to block any pending requests
        isLoggingOutRef.current = true;

        // Trigger logout redirect (awaited so re-auth is deterministic — AC7/8).
        // performLogout evicts MSAL's interaction lock and retries once if the
        // redirect's preflight throws interaction_in_progress (AC11), so the
        // redirect fires even when a concurrent interaction set that lock.
        await performErrorLogout(
          instance,
          'Token refresh failed after all retries'
        );
        return null;
      }
    }

    return null;
  }, [instance, setAccessToken]);

  /**
   * Ensures a valid token is available before making a request
   * MSAL handles token expiration internally - acquireTokenSilent returns cached token
   * if valid, or refreshes if expired.
   * Uses a shared promise to prevent multiple simultaneous token acquisition requests.
   */
  const ensureValidToken = React.useCallback(async (): Promise<
    string | null
  > => {
    // If a token acquisition is already in progress, wait for it
    if (tokenAcquisitionPromiseRef.current) {
      return tokenAcquisitionPromiseRef.current;
    }

    // Start token acquisition - MSAL will return cached token if valid or refresh if expired
    tokenAcquisitionPromiseRef.current = acquireValidToken().finally(() => {
      tokenAcquisitionPromiseRef.current = null;
    });

    return tokenAcquisitionPromiseRef.current;
  }, [acquireValidToken]);

  React.useEffect(() => {
    if (accessToken?.token) {
      // Authorization header is intentionally NOT set on defaults — the request
      // interceptor sets a fresh token per-request via ensureValidToken().
      // Setting it on defaults caused stale tokens to leak through when the
      // inProgress bypass returned config without refreshing.
      setIsAccessTokenSet(true);
      isLoggingOutRef.current = false;
    } else {
      setIsAccessTokenSet(false);
    }
  }, [accessToken, setIsAccessTokenSet]);

  // Acquire token on mount via ensureValidToken (replaces legacy acquireToken)
  React.useEffect(() => {
    if (
      inProgress === InteractionStatus.None &&
      instance.getAllAccounts().length > 0
    ) {
      ensureValidToken();
    }
  }, [inProgress, instance, ensureValidToken]);

  const clients = [axios, dispatchClient];

  configureAxiosDefaults(clients);

  // Set up interceptors
  React.useEffect(() => {
    const requestInterceptorIds: number[] = [];
    const responseInterceptorIds: number[] = [];

    clients.forEach((client) => {
      // Request interceptor to proactively check token before each request
      const requestInterceptorId = client.interceptors.request.use(
        async function onRequest(config: AxiosRequestConfig) {
          // Test-only: reproduce the bootstrap freeze for DEV/UAT. The real
          // bug is a *dispatched* GET /dispatch/auth/users whose response is
          // black-holed; axios bounds that with dispatchClient's response
          // timeout (BOOTSTRAP_HTTP_TIMEOUT_MS). Hanging here in the request
          // interceptor would sit *before* dispatch, where the response
          // timeout can never fire — an unfaithful repro that just hangs
          // forever. Instead reproduce the timeout's *outcome*: reject with
          // the same ECONNABORTED AxiosError axios emits, so the request fails
          // fast into the React Query retry -> error -> BootstrapError
          // recovery path. Gated identically to the other AUTH_TEST flags —
          // runtime-inert when AUTH_TEST_MODE is false (the branch stays in the
          // bundle but never executes; AUTH_TEST_MODE is a runtime value, so it
          // is not tree-shaken).
          if (
            TEST_FLAGS.FORCE_USERS_STALL &&
            (config.url || '').includes('/auth/users')
          ) {
            // eslint-disable-next-line no-console
            console.log(
              'TEST: Simulating black-holed GET /auth/users — aborting like the bootstrap timeout'
            );
            await new Promise<void>((_resolve, reject) => {
              setTimeout(
                () =>
                  // Mirror the ECONNABORTED shape axios emits on a real
                  // response timeout. AxiosError is type-only in this axios
                  // version, so build the rejection without its constructor.
                  reject(
                    Object.assign(
                      new Error(
                        'Simulated bootstrap timeout (AUTH_TEST_FORCE_USERS_STALL)'
                      ),
                      { code: 'ECONNABORTED' }
                    )
                  ),
                TEST_USERS_STALL_ABORT_MS
              );
            });
          }

          // Skip token check for requests that don't need authorization
          if (config.headers?.['Skip-Auth-Check']) {
            delete config.headers['Skip-Auth-Check'];
            return config;
          }

          // Block all requests if logout is in progress
          if (isLoggingOutRef.current) {
            return Promise.reject({
              message: 'Logout in progress - request blocked',
              isAuthError: true,
            });
          }

          // If an auth flow is active (login, redirect handling), wait for it
          // to complete before checking the token. Returning config immediately
          // would send the request without a fresh Authorization header.
          if (inProgressRef.current !== InteractionStatus.None) {
            if (AUTH_TEST_MODE) {
              // eslint-disable-next-line no-console
              console.warn(
                `[Auth] Request queued — auth flow in progress (${inProgressRef.current})`
              );
            }
            await new Promise<void>((resolve, reject) => {
              const maxWaitMs = 10_000;
              const pollMs = 100;
              let elapsed = 0;
              const signal = config.signal as AbortSignal | undefined;
              const onAbort = () =>
                reject({
                  message: 'Request aborted while waiting for auth flow',
                  isAuthError: true,
                });
              if (signal?.aborted) {
                onAbort();
                return;
              }
              signal?.addEventListener('abort', onAbort);
              const check = () => {
                if (signal?.aborted) return;
                if (inProgressRef.current === InteractionStatus.None) {
                  signal?.removeEventListener('abort', onAbort);
                  resolve();
                } else if (elapsed >= maxWaitMs) {
                  signal?.removeEventListener('abort', onAbort);
                  reject({
                    message: 'Auth flow did not complete in time',
                    isAuthError: true,
                  });
                } else {
                  elapsed += pollMs;
                  setTimeout(check, pollMs);
                }
              };
              check();
            });
          }

          // If there are no accounts, user is not logged in - skip token check
          // This allows the app to load without forcing logout before login
          if (instance.getAllAccounts().length === 0) {
            return config;
          }

          // Proactively ensure we have a valid token before making the request
          const validToken = await ensureValidToken();

          if (!validToken) {
            // acquireValidToken already set the logout flag and awaited
            // performErrorLogout on the exhaustion path. Both bootstrap GETs
            // share that one acquisition, so re-firing logout here would
            // collide with the in-flight logoutRedirect and emit a spurious
            // LogoutRedirectFailed. If a logout is already underway, just
            // reject; otherwise this is the first observer, so drive it.
            if (!isLoggingOutRef.current) {
              isLoggingOutRef.current = true;
              await performErrorLogout(
                instance,
                'Session expired. Please log in again.'
              );
            }
            return Promise.reject({
              message: 'Session expired - redirecting to login',
              isAuthError: true,
            });
          }

          if (config.headers) {
            config.headers.Authorization = `Bearer ${validToken}`;
          }

          return config;
        },
        function onRequestError(error) {
          return Promise.reject(error);
        }
      );
      requestInterceptorIds.push(requestInterceptorId);

      // Response interceptor for handling auth errors
      const responseInterceptorId = client.interceptors.response.use(
        function onSuccess(response: AxiosResponse) {
          if (response.status >= 200 && response.status < 300) {
            return response;
          }

          return Promise.reject(response);
        },
        function onError(error: AxiosError) {
          const FORBIDDEN = 403,
            UNAUTHORIZED = 401,
            BADREQUEST = 400,
            CONFLICT = 409;

          if (error?.response?.status === FORBIDDEN) {
            return Promise.reject({
              error,
              message: 'You are not authorized to complete this request',
            });
          }

          if (error?.response?.status === UNAUTHORIZED) {
            return Promise.reject({
              error,
              message: 'You are not authorized to complete this request',
            });
          }

          if (error?.response?.status === BADREQUEST) {
            return Promise.reject({
              error,
              message: error.response?.data?.detail || 'Bad request',
            });
          }

          if (error?.response?.status === CONFLICT) {
            return Promise.reject({
              error,
              message: error.response?.data?.detail || 'Conflict',
            });
          }

          return Promise.reject(error);
        }
      );
      responseInterceptorIds.push(responseInterceptorId);
    });

    // Cleanup interceptors on unmount or when dependencies change
    return () => {
      clients.forEach((client, index) => {
        client.interceptors.request.eject(requestInterceptorIds[index]);
        client.interceptors.response.eject(responseInterceptorIds[index]);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ensureValidToken]);

  return <>{children}</>;
}

export { AxiosConfiguration };
